import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Button from '../components/atoms/Button';
import {
  RootState, setTicketsSelected, setOrderItemData, useGetOrderByCustomIdQuery, useGetPaymentByOrderIdQuery,
} from '../store';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import Image from '../components/atoms/Image';
import AdminSectionTitle from '../components/AdminSectionTitle';
import EventDTO from '../models/event';
import LoadingLottie from '../components/atoms/LoadingLottie';
import { setSelectedEvent } from '../store/slices/events';
import { parseFriendlyDate } from '../utils/dates';
import BackgroundBannerImage from '../components/BackgroundBannerImage';

const CreateOrderError: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const selectedEvent = useSelector((state: RootState) => state.events.selectedEvent);
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [searchParams] = useSearchParams();
  const customId = searchParams.get('external_reference');
  const { data: order, isLoading: orderLoading } = useGetOrderByCustomIdQuery(
    { customId: customId || '' },
    { skip: !customId },
  );
  const { data: paymentData, isLoading: paymentLoading } = useGetPaymentByOrderIdQuery(
    { orderId: order?.id || '', eventId: event?.id || '', businessId: event?.business_id || '' },
    { skip: !order?.id },
  );
  const isBackoffice = useLocation().pathname.includes('admin');
  const inPerson = useLocation().pathname.includes('in-person');

  useEffect(() => {
    if (order) {
      return setEvent(order.event);
    }
    return setEvent(selectedEvent);
  }, [order, selectedEvent]);

  useEffect(() => {
    const savedTickets = localStorage.getItem(`${event?.id}_selectedTickets`);
    const savedOrderItemData = localStorage.getItem(`${event?.id}_orderItemData`);
    if (event) {
      if (savedOrderItemData && savedTickets) {
        dispatch(setSelectedEvent(event));
        dispatch(setTicketsSelected(JSON.parse(savedTickets)));
        dispatch(setOrderItemData(JSON.parse(savedOrderItemData)));
        navigate(`/events/${event?.slug}/orders/new/checkout`);
      } else {
        navigate(`/events/${event?.slug}/orders/new/${event?.has_numerated_seats ? 'seats' : 'tickets'}`);
      }
    }
  }, [event, paymentData]);

  if (!event) {
    console.error('no selected event');
  }

  if (orderLoading || paymentLoading) {
    return <LoadingLottie />;
  }

  const handleTryAgain = () => {
    if (isBackoffice) {
      navigate(`/admin/${event?.business_id}/events/${event?.id}/orders/new/tickets`);
    } else {
      navigate(`/events/${event?.slug}/`);
    }
  };

  return (
    <>
      {event && !isBackoffice && <BackgroundBannerImage event={event} />}
      <div className="grid grid-cols-1 lg:grid-cols-11 my-6 lg:gap-12 lg:px-8 text-text-primary min-h-[calc(100vh-200px)]">
        {inPerson && (
          <div className='col-span-11'>
            <AdminSectionTitle
              title="Venta presencial"
              subtitle={`${event?.name}`}
            />
          </div>
        )}
        {!inPerson && (
          <div className="col-span-4 flex flex-col gap-4 w-full mb-6">
            <Carrousel
              images={[{ url: event?.banner_image || '', eventId: event?.id || '' }]}
            />
            <div className='glass-surface text-center lg:text-left'>
              <h1 className='text-3xl font-medium'>{event?.name}</h1>
              <span className='text-xl text-text-primary'>{parseFriendlyDate(event?.date || '', true, true)}</span>
            </div>
          </div>
        )}
        <div className='col-span-7 flex flex-col gap-6'>
          <div className="flex flex-col md:flex-row gap-6 glass-surface">
            <div className="flex items-center justify-center p-4">
              <Image
                src="/x-error.svg"
                alt="error"
                className="w-auto h-auto max-w-[160px] object-contain"
              />
            </div>
            <div className="flex flex-col gap-4 p-2 justify-center flex-start text-center md:text-left">
              <h1>Tu solicitud no pudo ser generada de forma correcta</h1>
              <div>
                <p>
                  Debido a algún inconveniente no pudimos procesar tu solicitud de
                  entradas de forma correcta, por lo que te pedimos que intentes
                  nuevamente en realizar dicho pedido.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="primary" onClick={handleTryAgain}>
              Reintentar
            </Button>
            <div className="px-3 text-sm text-center">
              <p>
                Si el problema persiste te pedimos que te comuniques a nuestro
                <a className="underline" href="https://wa.me/542645515045" target="_blank">
                  WhatsApp
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrderError;
